/**
 * useTaskDates - Date field changes (due date, estimated duration, timestamps)
 */

import {
  validateDueDateChange,
  validateStartDateChange,
  validateEstimatedDurationChange,
  validateGanttDrag,
} from './useDateValidation';
import {
  getEffectiveDurationMinutes,
  MIN_DURATION_MINUTES,
} from '../../../../../utils/taskDuration';
import { toIsoDateString } from '../../../../../utils/missionDates';

export function useTaskDates(localSprint, setLocalSprint, recordAction, onDateValidationError) {
  const handleDueDateChange = (taskId, dueDate) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldDueDate = task?.dueDate;
    const normalizedDue = toIsoDateString(dueDate);

    if (oldDueDate === normalizedDue) return;

    const validation = validateDueDateChange(task, normalizedDue);
    if (!validation.valid) {
      onDateValidationError?.(validation.error);
      return;
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t =>
          t.id === taskId ? { ...t, dueDate: normalizedDue, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_DUE_DATE',
        description: `${taskName}: Due date changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, dueDate: oldDueDate } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, dueDate: normalizedDue } : t),
            };
          });
        },
      });
    }
  };

  const handleEstimatedDurationMinutesChange = (taskId, estimatedDurationMinutes) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldMinutes = task?.estimatedDurationMinutes;

    if (oldMinutes === estimatedDurationMinutes) return;

    const validation = validateEstimatedDurationChange(task, estimatedDurationMinutes);
    if (!validation.valid) {
      onDateValidationError?.(validation.error);
      return;
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t =>
          t.id === taskId ? { ...t, estimatedDurationMinutes, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_ESTIMATED_DURATION',
        description: `${taskName}: Estimate changed`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, estimatedDurationMinutes: oldMinutes } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, estimatedDurationMinutes } : t),
            };
          });
        },
      });
    }
  };

  const createDateChangeHandler = (fieldName, actionType, description, validateFn = null) => (taskId, dateValue) => {
    const task = localSprint?.tasks?.find(t => t.id === taskId);
    const oldValue = task?.[fieldName];
    const normalized = toIsoDateString(dateValue);

    if (oldValue === normalized) return;

    if (validateFn) {
      const validation = validateFn(task, normalized);
      if (!validation.valid) {
        onDateValidationError?.(validation.error);
        return;
      }
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      const tasks = prev.tasks || [];
      return {
        ...prev,
        tasks: tasks.map(t =>
          t.id === taskId ? { ...t, [fieldName]: normalized, updatedAt: new Date().toISOString() } : t
        ),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: actionType,
        description: `${taskName}: ${description}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, [fieldName]: oldValue } : t),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t => t.id === taskId ? { ...t, [fieldName]: normalized } : t),
            };
          });
        },
      });
    }
  };

  const handleCreatedAtChange = createDateChangeHandler('createdAt', 'CHANGE_CREATED_AT', 'Created date changed', validateStartDateChange);
  const handleStartedAtChange = createDateChangeHandler('startedAt', 'CHANGE_STARTED_AT', 'Start date changed', validateStartDateChange);
  const handleCompletedAtChange = createDateChangeHandler('completedAt', 'CHANGE_COMPLETED_AT', 'Completed date changed');
  const handleUpdatedAtChange = createDateChangeHandler('updatedAt', 'CHANGE_UPDATED_AT', 'Updated date changed');
  const handleTaskStartDateChange = createDateChangeHandler('startDate', 'CHANGE_START_DATE', 'Start date changed', validateStartDateChange);
  const handleTaskEndDateChange = createDateChangeHandler('endDate', 'CHANGE_END_DATE', 'End date changed');

  /**
   * @param {number} deltaOrNewDurationMinutes - delta minutes (move, resize-start) or new total minutes (resize-end)
   */
  const handleTaskDateRangeChange = (taskId, newStartDate, deltaOrNewDurationMinutes, dragType) => {
    const tasks = localSprint?.tasks || [];
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const thirdArg =
      dragType === 'resize-end'
        ? Math.max(MIN_DURATION_MINUTES, deltaOrNewDurationMinutes)
        : dragType === 'resize-start'
          ? deltaOrNewDurationMinutes
          : 0;

    const validation = validateGanttDrag(task, newStartDate, thirdArg, dragType);
    if (!validation.valid) {
      onDateValidationError?.(validation.error);
      return;
    }

    const now = new Date().toISOString();
    let updates = {};
    let oldValues = {};
    let description = '';

    if (dragType === 'move') {
      const newStartValue = newStartDate ? newStartDate.toISOString() : null;

      oldValues = { startedAt: task.startedAt };
      updates = { startedAt: newStartValue, updatedAt: now };
      description = 'Start date changed';
    } else if (dragType === 'resize-start') {
      const newStartValue = newStartDate ? newStartDate.toISOString() : null;

      const oldMinutes = getEffectiveDurationMinutes(task) || MIN_DURATION_MINUTES;
      const newMinutes = Math.max(MIN_DURATION_MINUTES, oldMinutes - deltaOrNewDurationMinutes);

      oldValues = { startedAt: task.startedAt, estimatedDurationMinutes: task.estimatedDurationMinutes };
      updates = { startedAt: newStartValue, estimatedDurationMinutes: newMinutes, updatedAt: now };
      description = 'Start date and duration changed';
    } else if (dragType === 'resize-end') {
      const newEstimate = Math.max(MIN_DURATION_MINUTES, deltaOrNewDurationMinutes);

      oldValues = { estimatedDurationMinutes: task.estimatedDurationMinutes };
      updates = { estimatedDurationMinutes: newEstimate, updatedAt: now };
      description = 'Duration changed';
    }

    setLocalSprint(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tasks: prev.tasks.map(t =>
          t.id === taskId ? { ...t, ...updates } : t
        ),
      };
    });

    if (recordAction) {
      const taskName = task?.name || 'Task';
      recordAction({
        type: 'CHANGE_DATE_RANGE',
        description: `${taskName}: ${description}`,
        undo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t =>
                t.id === taskId ? { ...t, ...oldValues } : t
              ),
            };
          });
        },
        redo: () => {
          setLocalSprint(prev => {
            if (!prev) return prev;
            return {
              ...prev,
              tasks: prev.tasks.map(t =>
                t.id === taskId ? { ...t, ...updates } : t
              ),
            };
          });
        },
      });
    }
  };

  return {
    handleDueDateChange,
    handleEstimatedDurationMinutesChange,
    handleCreatedAtChange,
    handleStartedAtChange,
    handleCompletedAtChange,
    handleUpdatedAtChange,
    handleTaskStartDateChange,
    handleTaskEndDateChange,
    handleTaskDateRangeChange,
  };
}

export default useTaskDates;
